import { formatDisplayDate } from "./dateFormat";
import { getSettlementSummary } from "./settlement";

const INVOICE_CONTACT = {
  address: "29/37 THIRUNEERMALAI MAIN ROAD Chennai",
  phone: "+919176776108",
  email: "sadiqwin@gmail.com",
  state: "33-Tamil Nadu",
};

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatMoney(value) {
  return `₹ ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)}`;
}

function formatQuantity(value) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 3 }).format(Number(value) || 0);
}

function formatPhone(value) {
  const phone = String(value ?? "").trim();
  if (!phone) return "";
  if (phone.startsWith("+")) return phone;
  const digits = phone.replace(/\D/g, "");
  if (/^[6-9]\d{9}$/.test(digits)) return `+91${digits}`;
  if (/^91[6-9]\d{9}$/.test(digits)) return `+${digits}`;
  return phone;
}

function integerWords(value) {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (value === 0) return "Zero";
  if (value < 20) return ones[value];
  if (value < 100) return `${tens[Math.floor(value / 10)]}${value % 10 ? ` ${ones[value % 10]}` : ""}`;
  if (value < 1000) {
    const remainder = value % 100;
    return `${ones[Math.floor(value / 100)]} Hundred${remainder ? ` and ${integerWords(remainder)}` : ""}`;
  }
  for (const [size, name] of [[10000000, "Crore"], [100000, "Lakh"], [1000, "Thousand"]]) {
    if (value >= size) {
      const remainder = value % size;
      return `${integerWords(Math.floor(value / size))} ${name}${remainder ? ` ${integerWords(remainder)}` : ""}`;
    }
  }
  return "";
}

export function amountInWords(amount) {
  const paiseTotal = Math.max(0, Math.round((Number(amount) || 0) * 100));
  const rupees = Math.floor(paiseTotal / 100);
  const paise = paiseTotal % 100;
  return `${integerWords(rupees)} Rupees${paise ? ` and ${integerWords(paise)} Paise` : ""} only`;
}

function stateFromGst(gstNumber) {
  const stateCode = String(gstNumber ?? "").trim().slice(0, 2);
  if (!/^\d{2}$/.test(stateCode)) return INVOICE_CONTACT.state;
  return stateCode === "33" ? INVOICE_CONTACT.state : stateCode;
}

function customerDetails(entry) {
  const lines = String(entry?.customer ?? "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const name = String(entry?.customerName || lines[0] || "Walk-in").trim();
  const lastLine = lines[lines.length - 1] || "";
  const fallbackPhone = /^\+?[\d\s()-]{10,}$/.test(lastLine) ? lastLine : "";
  const phone = String(entry?.customerPhone || fallbackPhone).trim();
  const address = [entry?.customerAddress, entry?.customerAddress2]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join("\n") || lines.slice(1).filter((line) => line !== phone && line !== fallbackPhone).join("\n");
  return { name, phone: formatPhone(phone), address };
}

export function buildInvoiceHtmlDocument({ entry, storeName = "Alif Protiens", storeGst = "", customerGst = "" }) {
  const bill = entry || {};
  const customer = customerDetails(bill);
  const items = Array.isArray(bill.items) ? bill.items : [];
  const gross = items.reduce((sum, item) => sum + (Number(item?.amount) || (Number(item?.qty) || 0) * (Number(item?.rate) || 0)), 0);
  const discount = Math.max(0, Number(bill.discountAmount) || 0);
  const total = Number.isFinite(Number(bill.total)) ? Number(bill.total) : Math.max(0, gross - discount);
  const settlement = getSettlementSummary(total, bill.payments);
  const adjustments = settlement.discountAmount + settlement.knockOffAmount;
  const quantity = items.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0);
  const businessName = String(storeName || "Alif Protiens").trim();
  const billNumber = String(bill.billNo || "").trim();
  const date = formatDisplayDate(bill.saleDate || "");
  const placeOfSupply = stateFromGst(customerGst || storeGst);
  const itemRows = items.map((item, index) => {
    const amount = Number(item?.amount) || (Number(item?.qty) || 0) * (Number(item?.rate) || 0);
    return `<tr>
      <td>${index + 1}</td>
      <td class="item-name">${escapeHtml(item?.material || item?.name || "")}</td>
      <td>${escapeHtml(item?.hsn || item?.hsnCode || "")}</td>
      <td class="number">${formatQuantity(item?.qty)}</td>
      <td class="number">${escapeHtml(item?.unit || item?.uom || "")}</td>
      <td class="number">${formatMoney(item?.rate)}</td>
      <td class="number">${formatMoney(amount)}</td>
    </tr>`;
  }).join("");
  const addressLines = customer.address.split(/\r?\n/).filter(Boolean).map((line) => `<div>${escapeHtml(line)}</div>`).join("");

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Tax Invoice ${escapeHtml(billNumber)}</title>
  <style>
    @page { size: A4; margin: 16mm 12mm 18mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { background: #fff; color: #111; font: 10.5pt Arial, Helvetica, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .invoice { width: 100%; }
    .business-name { font-size: 18pt; font-weight: 700; margin-bottom: 3px; }
    .business-details { line-height: 1.45; }
    .title { margin-top: 5px; border-top: 1px solid #4a1e10; padding: 3px 0 0; text-align: center; font-size: 18pt; font-weight: 700; color: #4a1e10; }
    .parties { display: grid; grid-template-columns: 59% 41%; gap: 0; margin-top: 13px; min-height: 51mm; line-height: 1.35; }
    .parties .heading { font-weight: 700; margin-bottom: 6px; }
    .bill-to .customer-name { font-weight: 700; margin-bottom: 6px; }
    .bill-to .detail { margin-top: 6px; }
    .invoice-details { text-align: right; }
    .invoice-details .detail { margin-top: 3px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    thead { display: table-header-group; }
    th { padding: 5px 4px; background: #4a1e10; color: white; font-weight: 700; text-align: left; white-space: nowrap; border-right: 1px solid #744234; }
    th:last-child { border-right: 0; }
    td { padding: 5px 4px; vertical-align: top; }
    tbody tr { break-inside: avoid; page-break-inside: avoid; }
    .item-name { font-weight: 700; }
    .number { text-align: right; }
    .total-row { border-top: 1px solid #aaa; border-bottom: 1px solid #777; font-weight: 700; }
    .total-row td { padding-top: 7px; padding-bottom: 7px; }
    .footer-grid { display: grid; grid-template-columns: 53% 47%; gap: 0; margin-top: 16px; break-inside: avoid; page-break-inside: avoid; }
    .section-heading { font-weight: 700; margin-bottom: 7px; }
    .amount-words { padding-right: 18px; line-height: 1.3; }
    .terms { margin-top: 14px; padding-right: 18px; }
    .terms .text { line-height: 1.3; }
    .summary-row { display: flex; justify-content: space-between; gap: 10px; padding: 3px 4px; }
    .summary-row strong { white-space: nowrap; }
    .summary-row.total { background: #4a1e10; color: #fff; font-weight: 700; }
    .summary-row.balance { border-bottom: 1px solid #777; padding-bottom: 7px; }
    .signatory { text-align: center; margin-top: 14px; }
    .signature-space { height: 29mm; }
    .signatory-label { font-weight: 700; }
    @media screen { body { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 16mm 12mm 18mm; box-shadow: 0 0 20px #0002; } }
  </style>
</head>
<body>
  <main class="invoice">
    <header>
      <div class="business-name">${escapeHtml(businessName)}</div>
      <div class="business-details">
        <div>${escapeHtml(INVOICE_CONTACT.address)}</div>
        <div>Phone no.: ${escapeHtml(INVOICE_CONTACT.phone)}</div>
        <div>Email: ${escapeHtml(INVOICE_CONTACT.email)}</div>
        ${storeGst ? `<div>GSTIN: ${escapeHtml(storeGst)}</div>` : ""}
        <div>State: ${escapeHtml(stateFromGst(storeGst))}</div>
      </div>
    </header>
    <div class="title">Tax Invoice</div>
    <section class="parties">
      <div class="bill-to">
        <div class="heading">Bill To</div>
        <div class="customer-name">${escapeHtml(customer.name)}</div>
        ${addressLines}
        ${customer.phone ? `<div class="detail">Contact No.: ${escapeHtml(customer.phone)}</div>` : ""}
        ${customerGst ? `<div class="detail">GSTIN Number: ${escapeHtml(customerGst)}</div><div class="detail">State: ${escapeHtml(stateFromGst(customerGst))}</div>` : ""}
      </div>
      <div class="invoice-details">
        <div class="heading">Invoice Details</div>
        <div class="detail">Invoice No.: ${escapeHtml(billNumber)}</div>
        <div class="detail">Date: ${escapeHtml(date)}</div>
        <div class="detail">Place of Supply: ${escapeHtml(placeOfSupply)}</div>
      </div>
    </section>
    <table>
      <colgroup><col style="width: 5%"><col style="width: 30%"><col style="width: 12%"><col style="width: 13%"><col style="width: 12%"><col style="width: 14%"><col style="width: 14%"></colgroup>
      <thead><tr><th>#</th><th>Item name</th><th>HSN/ SAC</th><th class="number">Quantity</th><th class="number">Unit</th><th class="number">Price/ unit</th><th class="number">Amount</th></tr></thead>
      <tbody>${itemRows}
        <tr class="total-row"><td></td><td>Total</td><td></td><td class="number">${formatQuantity(quantity)}</td><td></td><td></td><td class="number">${formatMoney(gross)}</td></tr>
      </tbody>
    </table>
    <div class="footer-grid">
      <div>
        <div class="section-heading">Invoice Amount In Words</div>
        <div class="amount-words">${escapeHtml(amountInWords(total))}</div>
        <div class="terms"><div class="section-heading">Terms And Conditions</div><div class="text">Thank you for doing business with us.</div></div>
      </div>
      <div>
        <div class="summary-row"><span>Sub Total</span><strong>${formatMoney(gross)}</strong></div>
        ${discount ? `<div class="summary-row"><span>Discount</span><strong>− ${formatMoney(discount)}</strong></div>` : ""}
        <div class="summary-row total"><span>Total</span><strong>${formatMoney(total)}</strong></div>
        <div class="summary-row"><span>Received</span><strong>${formatMoney(settlement.paidAmount)}</strong></div>
        ${adjustments ? `<div class="summary-row"><span>Adjustments</span><strong>${formatMoney(adjustments)}</strong></div>` : ""}
        <div class="summary-row balance"><span>Balance</span><strong>${formatMoney(settlement.pendingAmount)}</strong></div>
        <div class="signatory">For: ${escapeHtml(businessName)}<div class="signature-space"></div><div class="signatory-label">Authorized Signatory</div></div>
      </div>
    </div>
  </main>
</body>
</html>`;
}
